import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useSearch } from '@tanstack/react-router'
import { Eye, EyeOff, LoaderCircle } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useResetPasswordMutation } from '@/features/auth/api'
import { AuthLayout } from '@/features/auth/components/auth-layout'

const searchSchema = z.object({
  token: z.string().optional(),
})

export const Route = createFileRoute('/reset-password')({
  validateSearch: searchSchema,
  component: ResetPasswordPage,
})

const formSchema = z
  .object({
    new_password: z
      .string()
      .min(10, 'Password must be at least 10 characters')
      .max(128, 'Password must be at most 128 characters'),
    confirm: z.string(),
  })
  .refine((d) => d.new_password === d.confirm, {
    message: "Passwords don't match",
    path: ['confirm'],
  })

type FormValues = z.infer<typeof formSchema>

function ResetPasswordPage() {
  const { token } = useSearch({ from: '/reset-password' })
  const mutation = useResetPasswordMutation()
  const [showPw, setShowPw] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { new_password: '', confirm: '' },
  })

  function onSubmit(data: FormValues) {
    if (!token) return
    mutation.mutate({ token, new_password: data.new_password })
  }

  if (!token) {
    return (
      <AuthLayout>
        <div className="flex flex-col gap-6">
          <h1 className="text-2xl font-semibold tracking-tight">
            Invalid link
          </h1>
          <p className="text-sm text-muted-foreground">
            This password reset link is missing its token. Request a new link if
            you need to reset your password.
          </p>
          <Button asChild>
            <Link to="/forgot-password">Request a new link</Link>
          </Button>
        </div>
      </AuthLayout>
    )
  }

  if (mutation.isSuccess) {
    return (
      <AuthLayout>
        <div className="flex flex-col gap-6">
          <h1 className="text-2xl font-semibold tracking-tight">
            Password updated
          </h1>
          <p className="text-sm text-muted-foreground">
            You can now sign in with your new password.
          </p>
          <Button asChild>
            <Link to="/login">Back to sign in</Link>
          </Button>
        </div>
      </AuthLayout>
    )
  }

  const apiError =
    mutation.isError &&
    'This link is invalid or has expired. Request a new one.'

  return (
    <AuthLayout>
      <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Set a new password
          </h1>
          <p className="text-sm text-muted-foreground">
            Choose a strong password — at least 10 characters.
          </p>
        </div>

        {apiError && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {apiError}{' '}
            <Link
              to="/forgot-password"
              className="font-medium underline underline-offset-2"
            >
              Request a new link
            </Link>
            .
          </div>
        )}

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="new_password">New password</FieldLabel>
            <div className="relative">
              <Input
                id="new_password"
                type={showPw ? 'text' : 'password'}
                autoComplete="new-password"
                aria-invalid={!!errors.new_password}
                {...register('new_password')}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showPw ? 'Hide password' : 'Show password'}
              >
                {showPw ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </button>
            </div>
            {errors.new_password && (
              <FieldError>{errors.new_password.message}</FieldError>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="confirm">Confirm password</FieldLabel>
            <Input
              id="confirm"
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              aria-invalid={!!errors.confirm}
              {...register('confirm')}
            />
            {errors.confirm && (
              <FieldError>{errors.confirm.message}</FieldError>
            )}
          </Field>
        </FieldGroup>

        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending && (
            <LoaderCircle className="size-4 animate-spin" />
          )}
          Update password
        </Button>
      </form>
    </AuthLayout>
  )
}
